import { useEffect, useRef } from 'react';
import * as THREE from 'three';
import { useDispatch, useSelector } from 'react-redux';
import {
    resetOperation,
    setCommandMessage,
} from '../../../redux/operationSlice';
import { useInputText } from '../../../utils/useInputText';
import { generateUniqueId } from '../../../utils/generateUniqueId';
import { addAction } from '../../../redux/operationHistorySlice';
import { undo, redo } from '../../../utils/undoRedo'
import { setGlobalPolylineVerticesRef } from '../../../utils/sceneAction';
import { useStore } from 'react-redux';
import { getPerpendicularIntersection, isAxisAligned, getPreviewRayIntersection, getLineDirection, getDirectionalIntersection } from '../../../shared/geometry/getPrependicularIntersection';
import { COMMANDS } from '../../../utils/commonProperties';
import { createDrawingClickHandler, applyGridSnap, getOrthoSnappedPoint } from "./useDrawingEvents";
import { createSnapMarker } from '../../../shared/snap/createSnapMarker';
import { getSnappedPoint } from '../../../shared/snap/getSnappedPoint';
import { createEscapeHandler } from '../../../utils/escapeHandler';
import { DrawCommand } from '../../../modules/undoRedo';
import { addUserEntity } from '../../../redux/dxfSlice';

const createOffsetLine = (originalLine, lastVertex, distance = 5) => {
    const positions = originalLine.geometry.attributes.position.array;
    const start = new THREE.Vector3(positions[0], positions[1], positions[2]);
    const end = new THREE.Vector3(positions[3], positions[4], positions[5]);

    const lineDir = new THREE.Vector3().subVectors(end, start).normalize();
    const normal = new THREE.Vector3(-lineDir.y, lineDir.x, 0).normalize();

    // 1. Dik izdüşüm noktasını bul
    const projected = getPerpendicularIntersection(lastVertex, { start, end });

    // 2. targetLine segmenti içinde mi?
    const segmentLength = start.distanceTo(end);
    const projToStart = projected.distanceTo(start);
    const projToEnd = projected.distanceTo(end);
    const isOnSegment = Math.abs(projToStart + projToEnd - segmentLength) < 0.01;

    // 3. Ofset yönünü belirle
    const mid = new THREE.Vector3().addVectors(start, end).multiplyScalar(0.5);
    const toLast = new THREE.Vector3().subVectors(lastVertex, mid).normalize();
    const dot = normal.dot(toLast);

    // 🔁 Eğer dikme segmenti kesmiyorsa → yön ters çevrilmeli
    const adjustedNormal = (dot > 0) === isOnSegment ? normal : normal.multiplyScalar(-1);

    const offsetStart = start.clone().add(adjustedNormal.clone().multiplyScalar(distance));
    const offsetEnd = end.clone().add(adjustedNormal.clone().multiplyScalar(distance));

    const geometry = new THREE.BufferGeometry().setFromPoints([offsetStart, offsetEnd]);
    const material = new THREE.LineDashedMaterial({ color: 0x00ffff, dashSize: 5, gapSize: 3 });

    const offsetLine = new THREE.Line(geometry, material);
    offsetLine.computeLineDistances();
    offsetLine.name = 'offsetPreview';
    return offsetLine;
};

const useDrawPolyline = (scene, camera, renderer, snapPoints, options = {}) => {
    const store = useStore();
    const verticesRef = useRef([]);
    const tempLineRef = useRef(null);
    const snapMarkerRef = useRef(null);
    const lastMouse = useRef({ x: 0, y: 0 });
    const offsetLineRef = useRef(null); // SnapWall kopya çizgisi
    const intersectedLineRef = useRef(null); // Mavi olacak çizgi
    const hasInitialPointBeenAdded = useRef(false);
    const lastOffsetDirectionRef = useRef(null); // ✅ Son açılı offset çizgisinin yönü

    const snapState = useRef({ x: 0, y: 0 });
    const {
        enabled = () => true,
        initialPoint = null,
    } = options;

    const inputText = useInputText(scene, camera);
    const dispatch = useDispatch();

    const { commandType } = useSelector((state) => state.operation);
    const gridMode = useSelector((state) => state.mods.gridMode);
    const objectSnap = useSelector((state) => state.mods.objectSnap);

    const snapMode = useSelector((state) => state.mods.snapMode);
    const orthoMode = useSelector((state) => state.mods.orthoMode); // Ortho modu Redux'tan
    const { activeLayerName } = useSelector((state) => state.layer);
    const activeTabId = useSelector(state => state.projectTabs.activeTabId);
    const snapWall = true;

    function click(x, y) {
        lastMouse.current = { x, y };
        if (verticesRef.current.length === 0 && !initialPoint) {
            dispatch(setCommandMessage("İlk noktayı seçin"));
        }

        const mouseNDC = new THREE.Vector2(x, y);
        const worldPoint = new THREE.Vector3(x, y, 0).unproject(camera);

        const dynamicSnapPoints = snapPoints;
        let { finalPoint, snapped, snapSource } = getSnappedPoint(
            mouseNDC,
            worldPoint,
            dynamicSnapPoints,
            camera,
            renderer,
            { snapMode }
        );

        if (snapWall && offsetLineRef.current && verticesRef.current.length > 0) {
            const targetLine = offsetLineRef.current;
            const positions = targetLine.geometry.attributes.position.array;
            const start = new THREE.Vector3(positions[0], positions[1], positions[2]);
            const end = new THREE.Vector3(positions[3], positions[4], positions[5]);
            const lastVertex = verticesRef.current[verticesRef.current.length - 1];
            
            // Çizgi eksen hizalı mı kontrol et
            if (isAxisAligned(start, end)) {
                // Eksen hizalı çizgiler için
                if (lastOffsetDirectionRef.current) {
                    // Önceki açılı çizginin yönünde devam et
                    finalPoint = getDirectionalIntersection(lastVertex, lastOffsetDirectionRef.current, { start, end });
                } else {
                    // Normal perpendicular intersection kullan
                    finalPoint = getPerpendicularIntersection(lastVertex, { start, end });
                }
                // Eksen hizalı çizgiye tıklandığında açılı yön sıfırla
                lastOffsetDirectionRef.current = null;
            } else {
                // Açılı çizgiler için
                if (lastOffsetDirectionRef.current) {
                    // Önceki açılı çizginin yönünde devam et
                    finalPoint = getDirectionalIntersection(lastVertex, lastOffsetDirectionRef.current, { start, end });
                } else {
                    // Mouse yönünde kesişim hesapla
                    finalPoint = getPreviewRayIntersection(lastVertex, worldPoint, { start, end });
                }
                // Açılı çizginin yönünü sakla (sonraki çizim için)
                lastOffsetDirectionRef.current = getLineDirection(start, end);
            }
        } else if (!snapped) {
            finalPoint = gridMode ? applyGridSnap(worldPoint) : worldPoint;
        }

        if (orthoMode && verticesRef.current.length > 0) {
            const lastVertex = verticesRef.current[verticesRef.current.length - 1];
            finalPoint = getOrthoSnappedPoint(lastVertex, finalPoint);
        }

        if (snapMarkerRef.current) {
            scene.remove(snapMarkerRef.current);
            snapMarkerRef.current.geometry.dispose();
            snapMarkerRef.current.material.dispose();
            snapMarkerRef.current = null;
        }

        if (snapped && snapSource) {
            const marker = createSnapMarker(snapSource);
            marker.position.copy(finalPoint);
            scene.add(marker);
            snapMarkerRef.current = marker;
        }

        // ❗ Eğer sadece initialPoint varsa ve bu tıklama ile 2. nokta ekleniyorsa → normal akış
        verticesRef.current.push(finalPoint);

        dispatch(addAction({
            type: 'vertexAdd',
            objectUUID: 'polylineDrawing',
            before: verticesRef.current.slice(0, -1).map(p => p.toArray()),
            after: verticesRef.current.map(p => p.toArray()),
        }));

        dispatch(setCommandMessage("Sonraki noktaları seçin. Bitirmek için Enter, iptal için ESC."));
        if (verticesRef.current.length === 1) {
            const geometry = new THREE.BufferGeometry().setFromPoints([verticesRef.current[0]]);
            const material = new THREE.LineBasicMaterial({ color: 0xff00ff });

            if (tempLineRef.current) {
                scene.remove(tempLineRef.current);
                tempLineRef.current.geometry.dispose();
                tempLineRef.current.material.dispose();
            }

            tempLineRef.current = new THREE.Line(geometry, material);
            scene.add(tempLineRef.current);
        }
    }
    useEffect(() => {
        setGlobalPolylineVerticesRef(verticesRef);
    }, []);

    useEffect(() => {
        if (initialPoint && verticesRef.current.length === 0 && !hasInitialPointBeenAdded.current) {
            const vector = new THREE.Vector3(initialPoint.x, initialPoint.y, 0);
            const ndc = vector.clone().project(camera); // Dünya → NDC
            click(ndc.x, ndc.y); // sadece ilk noktayı simüle et
            hasInitialPointBeenAdded.current = true;
        }
    }, [initialPoint]);

    useEffect(() => {
        if (commandType !== COMMANDS.DRAW_POLYLINE || !scene || !camera || !renderer) return;
        

        const domElement = renderer.domElement;
        dispatch(setCommandMessage("İlk noktayı seçin"));


        const handlePolylinePoint = (point) => {
            if (!enabled()) return;

            // Apply ortho mode if enabled
            if (orthoMode && verticesRef.current.length > 0) {
                const lastVertex = verticesRef.current[verticesRef.current.length - 1];
                point = getOrthoSnappedPoint(lastVertex, point);
            }

            verticesRef.current.push(point);

            dispatch(addAction({
                type: 'vertexAdd',
                objectUUID: 'polylineDrawing',
                before: verticesRef.current.slice(0, -1).map(p => p.toArray()),
                after: verticesRef.current.map(p => p.toArray()),
            }));

            dispatch(setCommandMessage("Sonraki noktaları seçin. Bitirmek için Enter, iptal için ESC."));
            if (verticesRef.current.length === 1) {
                const geometry = new THREE.BufferGeometry().setFromPoints([verticesRef.current[0]]);
                const material = new THREE.LineBasicMaterial({ color: 0xff00ff });

                if (tempLineRef.current) {
                    scene.remove(tempLineRef.current);
                    tempLineRef.current.geometry.dispose();
                    tempLineRef.current.material.dispose();
                }

                tempLineRef.current = new THREE.Line(geometry, material);
                scene.add(tempLineRef.current);
            }
        };

        const handleClick = createDrawingClickHandler({
            scene,
            camera,
            renderer,
            snapPoints,
            snapMarkerRef,
            gridMode,
            objectSnap,
            snapMode,
            onPointSelected: handlePolylinePoint
        });

        const handleMouseMove = (event) => {
            const rect = domElement.getBoundingClientRect();
            const x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
            const y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
            lastMouse.current = { x, y };

            const mouseNDC = new THREE.Vector2(x, y);
            const worldPoint = new THREE.Vector3(x, y, 0).unproject(camera);

            const dynamicSnapPoints = snapPoints
            const { finalPoint, snapped, snapSource } = getSnappedPoint(mouseNDC, worldPoint, dynamicSnapPoints, camera, renderer, { snapMode: snapMode });

            // Snap marker'ı temizle ve yenisini oluştur
            if (snapMarkerRef.current) {
                scene.remove(snapMarkerRef.current);
                snapMarkerRef.current.geometry.dispose();
                snapMarkerRef.current.material.dispose();
                snapMarkerRef.current = null;
            }

            if (snapped && snapSource) {
                const marker = createSnapMarker(snapSource);
                marker.position.copy(snapSource.position);
                scene.add(marker);
                snapMarkerRef.current = marker;
            }

            // Önizleme noktasını belirle
            let previewPoint = finalPoint;
            if (orthoMode && verticesRef.current.length > 0) {
                const lastVertex = verticesRef.current[verticesRef.current.length - 1];
                previewPoint = getOrthoSnappedPoint(lastVertex, finalPoint);
            }

            // Eğer en az bir vertex varsa geçici çizgiyi güncelle
            if (verticesRef.current.length > 0) {
                const previewPoints = [...verticesRef.current, previewPoint];
                const geometry = new THREE.BufferGeometry().setFromPoints(previewPoints);
                const material = new THREE.LineBasicMaterial({ color: 0xff00ff });

                if (tempLineRef.current) {
                    scene.remove(tempLineRef.current);
                    tempLineRef.current.geometry.dispose();
                    tempLineRef.current.material.dispose();
                }

                tempLineRef.current = new THREE.Line(geometry, material);
                scene.add(tempLineRef.current);
            }
            if (snapWall) {
                const raycaster = new THREE.Raycaster();
                raycaster.setFromCamera(mouseNDC, camera);

                // 🔹 Hem Line hem Polyline tipi nesneleri al
                const lines = scene.children.filter(
                    (obj) =>
                        obj.type === 'Line' &&
                        (obj.userData?.type === 'line' || obj.userData?.type === 'polyline')
                );

                const intersects = raycaster.intersectObjects(lines);

                // 🔹 Önceki intersected çizgiyi temizle
                if (intersectedLineRef.current) {
                    intersectedLineRef.current.material.color.set(0xffffff);
                    intersectedLineRef.current = null;
                }

                if (offsetLineRef.current) {
                    scene.remove(offsetLineRef.current);
                    offsetLineRef.current.geometry.dispose();
                    offsetLineRef.current.material.dispose();
                    offsetLineRef.current = null;
                }

                if (intersects.length > 0) {
                    const intersected = intersects[0].object;
                    const geometry = intersected.geometry;
                    const isPolyline = intersected.userData?.type === 'polyline';

                    let targetSegment = null;

                    if (isPolyline) {
                        // polyline üzerindeki en yakın kenarı bul
                        const vertices = geometry.attributes.position.array;
                        let minDist = Infinity;

                        for (let i = 0; i < vertices.length - 3; i += 3) {
                            const start = new THREE.Vector3(vertices[i], vertices[i + 1], vertices[i + 2]);
                            const end = new THREE.Vector3(vertices[i + 3], vertices[i + 4], vertices[i + 5]);

                            const closest = getPerpendicularIntersection(worldPoint, { start, end });
                            const dist = closest.distanceTo(worldPoint);

                            if (dist < minDist) {
                                minDist = dist;
                                targetSegment = { start, end };
                            }
                        }
                    } else {
                        // normal line ise
                        const pos = geometry.attributes.position.array;
                        targetSegment = {
                            start: new THREE.Vector3(pos[0], pos[1], pos[2]),
                            end: new THREE.Vector3(pos[3], pos[4], pos[5]),
                        };
                    }

                    if (targetSegment) {
                        // 🔹 geçici intersectedLine gibi davranması için sahte nesne oluştur
                        const tempGeometry = new THREE.BufferGeometry().setFromPoints([
                            targetSegment.start,
                            targetSegment.end,
                        ]);
                        const tempMaterial = new THREE.LineBasicMaterial({ color: 0x0000ff });
                        const tempLine = new THREE.Line(tempGeometry, tempMaterial);

                        scene.add(tempLine);
                        intersectedLineRef.current = tempLine;

                        if (verticesRef.current.length > 0) {
                            const lastVertex = verticesRef.current[verticesRef.current.length - 1];
                            const offsetLine = createOffsetLine(tempLine, lastVertex, 5);
                            scene.add(offsetLine);
                            offsetLineRef.current = offsetLine;
                        }
                    }
                }
            }
        };

        const escapeHandler = createEscapeHandler({
            scene,
            dispatch,
            tempObjectRef: tempLineRef,
            snapMarkerRef,
            inputText,
            additionalCleanup: () => {
                verticesRef.current = [];
                dispatch(setCommandMessage(""));
            }
        });

        const handleKeyDown = (e) => {
            if (e.key === 'Escape') {
                escapeHandler(e);
            } else if (e.key === 'Enter') {
                if (verticesRef.current.length > 1) {
                    const geometry = new THREE.BufferGeometry().setFromPoints(verticesRef.current);
                    const material = new THREE.LineBasicMaterial({ color: 0xffffff });
                    const line = new THREE.Line(geometry, material);
                    line.userData = {
                        id: generateUniqueId(),
                        isSelectable: true,
                        type: "polyline"
                    };
                    scene.add(line);

                    // Redux'a DXF entity olarak kaydet (JBZ'ye dahil edilecek)
                    dispatch(addUserEntity({
                        type: 'LWPOLYLINE',
                        vertices: verticesRef.current.map(p => ({ x: p.x, y: p.y, z: p.z || 0 })),
                        shape: false,
                        layer: activeLayerName || '0',
                        _userEntityId: line.userData.id,
                        _userDrawn: true,
                        _tabId: activeTabId || 'mimari',
                    }));

                    // Undo-redo kaydı
                    const manager = window.undoRedoManager;
                    if (manager) {
                        const tx = manager.beginTransaction('Draw Polyline');
                        manager.addCommand(new DrawCommand(line));
                        manager.commitTransaction(tx);
                    }

                    dispatch(addAction({
                        type: 'create',
                        objectUUID: line.uuid,
                        after: {
                            uuid: line.uuid,
                            points: verticesRef.current.map(p => ({ x: p.x, y: p.y, z: p.z })),
                            materialColor: 0xffffff,
                            type: 'polyline'
                        }
                    }));

                }

                if (tempLineRef.current) {
                    scene.remove(tempLineRef.current);
                    tempLineRef.current.geometry.dispose();
                    tempLineRef.current.material.dispose();
                    tempLineRef.current = null;
                }
                verticesRef.current = [];
                inputText.clear();
                dispatch(resetOperation());
                dispatch(setCommandMessage(""));
            }

            if (e.ctrlKey && e.key === 'z') {
                undo(scene, dispatch, store.getState); // ✅ redux state’in en güncel halini alır
            }
            if (e.ctrlKey && e.key === 'y') {
                redo(scene, dispatch, store.getState);
            }

            // ⬇️ Yön tuşları için ek özellikler
            const movementOffset = 5;
            if (snapMarkerRef.current) {
                const snapPos = snapMarkerRef.current.position.clone();
                const previousPos = verticesRef.current[verticesRef.current.length - 1];
                if (!previousPos) return;
                let newPos = new THREE.Vector3(previousPos.x, previousPos.y, 0);

                const isSnapRight = snapPos.x >= previousPos.x;
                const isSnapLeft = snapPos.x <= previousPos.x;
                const isSnapUp = snapPos.y >= previousPos.y;
                const isSnapDown = snapPos.y <= previousPos.y;

                if (e.key === 'a' && isSnapLeft) {
                    snapState.current.y = 0;
                    if (!snapState.current.x) {
                        newPos.x = snapPos.x + movementOffset;
                        snapState.current.x = 1;
                    } else if (snapState.current.x === 1) {
                        newPos.x = snapPos.x;
                        snapState.current.x = 2;
                    } else {
                        newPos.x = snapPos.x - movementOffset;
                        snapState.current.x = 0;
                    }
                } else if (e.key === 'd' && isSnapRight) {
                    snapState.current.y = 0;
                    if (!snapState.current.x) {
                        newPos.x = snapPos.x - movementOffset;
                        snapState.current.x = 1;
                    } else if (snapState.current.x === 1) {
                        newPos.x = snapPos.x;
                        snapState.current.x = 2;
                    } else {
                        newPos.x = snapPos.x + movementOffset;
                        snapState.current.x = 0;
                    }
                } else if (e.key === 'w' && isSnapUp) {
                    snapState.current.x = 0;
                    if (!snapState.current.y) {
                        newPos.y = snapPos.y - movementOffset;
                        snapState.current.y = 1;
                    } else if (snapState.current.y === 1) {
                        newPos.y = snapPos.y;
                        snapState.current.y = 2;
                    } else {
                        newPos.y = snapPos.y + movementOffset;
                        snapState.current.y = 0;
                    }
                } else if (e.key === 's' && isSnapDown) {
                    snapState.current.x = 0;
                    if (!snapState.current.y) {
                        newPos.y = snapPos.y + movementOffset;
                        snapState.current.y = 1;
                    } else if (snapState.current.y === 1) {
                        newPos.y = snapPos.y;
                        snapState.current.y = 2;
                    } else {
                        newPos.y = snapPos.y - movementOffset;
                        snapState.current.y = 0;
                    }
                }
            }

            if (e.key === 'q') {
                const previousPos = verticesRef.current[verticesRef.current.length - 1];
                if (!previousPos) return;

                const mouseWorld = new THREE.Vector3(
                    lastMouse.current.x,
                    lastMouse.current.y,
                    0
                ).unproject(camera);

                const direction = new THREE.Vector3().subVectors(mouseWorld, previousPos).normalize();

                const newPos = new THREE.Vector3().addVectors(
                    previousPos,
                    direction.multiplyScalar(movementOffset)
                );

                verticesRef.current.push(newPos);
            }

            if (e.key === 'e') {
                const previousPos = verticesRef.current[verticesRef.current.length - 1];
                const mouseWorld = new THREE.Vector3(lastMouse.current.x, lastMouse.current.y, 0).unproject(camera);

                const deltaX = mouseWorld.x - previousPos.x;
                const deltaY = mouseWorld.y - previousPos.y;

                const offsetLength = 7.07106781187;
                const dx = offsetLength / Math.sqrt(2);
                const dy = dx;

                let offset = new THREE.Vector3();
                if (deltaX >= 0 && deltaY >= 0) offset.set(dx, dy, 0);
                else if (deltaX < 0 && deltaY >= 0) offset.set(-dx, dy, 0);
                else if (deltaX < 0 && deltaY < 0) offset.set(-dx, -dy, 0);
                else offset.set(dx, -dy, 0);

                const newPos = new THREE.Vector3().addVectors(previousPos, offset);
                verticesRef.current.push(newPos);
            }
        };

        domElement.addEventListener('click', handleClick);
        domElement.addEventListener('mousemove', handleMouseMove);
        window.addEventListener('keydown', handleKeyDown);

        return () => {
            domElement.removeEventListener('click', handleClick);
            domElement.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [commandType, scene, camera, renderer, gridMode, objectSnap, snapPoints, dispatch]);

};

export default useDrawPolyline;
